<script>
  const ANCHOR_CLS = 'ada-validator__anchor--issue';
  const DATA_ISSUE = 'data-ada-validator-issue';
  const REASON_EMPTY_ALT = 'Alt text attribute is empty.';
  const REASON_MISSING_ALT = 'Missing alt attribute.';
  const SCAN_DEBOUNCE_MS = 400;
  const SETTLED_DEBOUNCE_MS = 800;
  import iconClose from '../snippets/icon-close.liquid?raw';
  import iconOpen from '../snippets/icon-open.liquid?raw';
  import AdaValidatorIssueList from '@/components/AdaValidatorIssueList.vue';

  export default {
    name: 'AdaValidator',
    components: {
      AdaValidatorIssueList
    },
    data() {
      return {
        issues: [],
        issuesTab: 'empty',
        overlaysVisible: true,
        nextIssueId: 1,
        panelExpanded: true,
        scanComplete: false
      };
    },
    computed: {
      panelToggleIconHtml() {
        return this.panelExpanded ? iconClose : iconOpen;
      },
      panelToggleAriaLabel() {
        return this.panelExpanded ? 'Collapse accessibility error details' : 'Expand accessibility error details';
      },
      issuesEmptyAlt() {
        return this.issues.filter(r => r.reason === REASON_EMPTY_ALT);
      },
      issuesMissingAlt() {
        return this.issues.filter(r => r.reason === REASON_MISSING_ALT);
      }
    },
    mounted() {
      this.debouncedScan = this.debounce(this.runScan, SCAN_DEBOUNCE_MS);
      this.debouncedMarkSettled = this.debounce(() => {
        this.scanComplete = true;
      }, SETTLED_DEBOUNCE_MS);
      this.onDomChurn = () => {
        this.debouncedScan();
        this.debouncedMarkSettled();
      };
      this.$nextTick(() => {
        window.setTimeout(() => this.runScan(), 0);
      });
      document.addEventListener('shopify:section:load', this.onDomChurn);
      document.addEventListener('shopify:section:unload', this.onDomChurn);
      document.addEventListener('shopify:section:reorder', this.onDomChurn);
      window.addEventListener('load', this.onDomChurn);
      this.domObserver = new MutationObserver(() => this.onDomChurn());
      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'alt']
      });
      this.debouncedMarkSettled();
    },
    beforeUnmount() {
      this.clearIssueMarkers();
      if (this.domObserver) {
        this.domObserver.disconnect();
        this.domObserver = null;
      }
      if (this.debouncedScan) {
        this.debouncedScan.cancel();
      }
      if (this.debouncedMarkSettled) {
        this.debouncedMarkSettled.cancel();
      }
      if (this.onDomChurn) {
        document.removeEventListener('shopify:section:load', this.onDomChurn);
        document.removeEventListener('shopify:section:unload', this.onDomChurn);
        document.removeEventListener('shopify:section:reorder', this.onDomChurn);
        window.removeEventListener('load', this.onDomChurn);
      }
    },
    methods: {
      debounce(fn, ms) {
        let t;
        const debounced = () => {
          clearTimeout(t);
          t = setTimeout(() => fn.call(this), ms);
        };
        debounced.cancel = () => {
          clearTimeout(t);
          t = null;
        };
        return debounced;
      },
      isValidatorExcludedImg(img) {
        if (img.classList.contains('ada-validator-thumb')) return true;
        if (img.getAttribute('data-ada-validator-ignore') === '1') return true;
        if (img.closest('#ada-validator-ui')) return true;
        return false;
      },
      isInvalidImgAlt(img) {
        if (img.getAttribute('role') === 'presentation') return { invalid: false };
        if (!img.hasAttribute('alt')) return { invalid: true, reason: REASON_MISSING_ALT };
        const raw = img.getAttribute('alt');
        if (raw == null || raw.trim() === '') {
          return {
            invalid: true,
            reason: REASON_EMPTY_ALT
          };
        }
        return { invalid: false };
      },
      getAnchor(img) {
        const block = img.closest('[data-block-id]');
        const anchor = block || img.closest('[id^="shopify-section-"]');
        if (!anchor) return null;
        const ui = document.getElementById('ada-validator-ui');
        if (ui && anchor.contains(ui)) return null;
        return anchor;
      },
      clearIssueMarkers() {
        document.querySelectorAll(`img[${DATA_ISSUE}]`).forEach(img => {
          img.removeAttribute(DATA_ISSUE);
          img.classList.remove('ada-validator-img');
        });
        document.querySelectorAll('.' + ANCHOR_CLS).forEach(el => el.classList.remove(ANCHOR_CLS));
      },
      clearHighlightVisualOnly() {
        document.querySelectorAll('.' + ANCHOR_CLS).forEach(el => el.classList.remove(ANCHOR_CLS));
        document.querySelectorAll(`img[${DATA_ISSUE}]`).forEach(img => img.classList.remove('ada-validator-img'));
      },
      applyHighlightVisual() {
        const seen = new Set();
        document.querySelectorAll(`img[${DATA_ISSUE}]`).forEach(img => {
          img.classList.add('ada-validator-img');
          const anchor = this.getAnchor(img);
          if (!anchor || seen.has(anchor)) return;
          seen.add(anchor);
          anchor.classList.add(ANCHOR_CLS);
        });
      },
      getValidatorUiRoot() {
        return document.getElementById('ada-validator-ui');
      },
      getFlaggedImageDebugInfo(img, issueId, reason) {
        const src = img.currentSrc || img.src || '';
        const imgClosest = img.closest('[id^="shopify-section-"]');
        const sectionHint = imgClosest ? imgClosest.id : null;
        let fileName = '';
        try {
          const path = new URL(src, window.location.href).pathname;
          fileName = path.split('/').pop() || src;
        } catch {
          fileName = src || '(no src)';
        }
        const par = img.parentElement;
        const parentHint = par ? `${par.tagName.toLowerCase()}${par.id ? '#' + par.id : ''}${par.className && String(par.className).trim() ? '.' + String(par.className).trim().split(/\s+/).slice(0, 3).join('.') : ''}` : null;
        return {
          issueId,
          reason,
          fileName,
          src,
          parentHint,
          sectionHint
        };
      },
      runScan() {
        const skipRoot = this.getValidatorUiRoot();
        this.clearIssueMarkers();
        this.nextIssueId = 1;
        const list = [];
        document.querySelectorAll('img').forEach(img => {
          if (skipRoot && skipRoot.contains(img)) return;
          if (this.isValidatorExcludedImg(img)) return;
          const { invalid, reason } = this.isInvalidImgAlt(img);
          if (!invalid) return;
          const id = String(this.nextIssueId++);
          img.setAttribute(DATA_ISSUE, id);
          const info = this.getFlaggedImageDebugInfo(img, id, reason || 'Alt issue');
          list.push({
            id: info.issueId,
            src: info.src,
            reason: info.reason,
            parentHint: info.parentHint,
            sectionHint: info.sectionHint,
            fileName: info.fileName
          });
        });
        this.issues = list;
        if (this.overlaysVisible) this.applyHighlightVisual();
      },
      onRescan() {
        this.runScan();
        this.scanComplete = false;
      },
      toggleOverlays() {
        this.overlaysVisible = !this.overlaysVisible;
        if (this.overlaysVisible) this.applyHighlightVisual();
        else this.clearHighlightVisualOnly();
      },
      scrollToIssue(id) {
        const img = document.querySelector(`img[${DATA_ISSUE}="${id}"]`);
        let target = null;
        if (img) {
          target = this.getAnchor(img) || img.closest('[id^="shopify-section-"]');
        }
        if (!target) {
          const row = this.issues.find(r => String(r.id) === String(id));
          const sectionId = row && row.sectionHint ? row.sectionHint : null;
          if (sectionId) {
            target = document.getElementById(sectionId);
          }
        }
        if (!target && img) {
          target = img;
        }
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn('[ADA Validator] no scroll target for issue id', id);
        }
      },
      toggleModal() {
        this.panelExpanded = !this.panelExpanded;
      },
      setIssuesTab(tab) {
        this.issuesTab = tab;
      }
    }
  };
</script>

<template>
  <teleport to="body">
    <div id="ada-validator-ui" class="ada-validator-app" role="region" aria-label="ADA validator theme editor tools" @mousedown.stop @pointerdown.stop @click.stop>
      <div class="ada-validator-panel">
        <div class="ada-validator-panel-header">
          <span class="ada-validator-title" id="ada-validator-panel-title">Accessibility Checker</span>
          <button type="button" class="ada-validator-icon" aria-controls="ada-validator-panel-details" :aria-expanded="panelExpanded" :aria-label="panelToggleAriaLabel" @click.stop="toggleModal" v-html="panelToggleIconHtml"></button>
        </div>
        <div id="ada-validator-panel-details" class="ada-validator-expand" :class="{ 'ada-validator-expand--open': panelExpanded }" role="region" aria-labelledby="ada-validator-panel-title" :aria-hidden="!panelExpanded" :inert="!panelExpanded">
          <div class="ada-validator-expand-inner">
            <div class="ada-validator-count">
              <div v-if="!scanComplete" class="ada-validator-scanning" role="status" aria-live="polite">
                <span class="ada-validator-scanning__glass">
                  <span class="ada-validator-scanning__shimmer" aria-hidden="true"></span>
                  <span class="ada-validator-scanning__label">Checking for Accessibility Errors</span>
                  <span class="ada-validator-scanning__dots" aria-hidden="true">
                    <span class="ada-validator-scanning__dot"></span>
                    <span class="ada-validator-scanning__dot"></span>
                    <span class="ada-validator-scanning__dot"></span>
                  </span>
                </span>
              </div>
              <p v-if="issues.length === 0 && scanComplete">No alt text issues found.</p>
              <p v-else-if="issues.length === 1 && scanComplete">This page has 1 alt text issue.</p>
              <p v-else-if="scanComplete">This page has {{ issues.length }} alt text issues ({{ issuesEmptyAlt.length }} empty alt attributes, {{ issuesMissingAlt.length }} missing alt attributes).</p>
            </div>
            <div v-if="scanComplete && issues.length" class="ada-validator-issue-tabs">
              <div class="ada-validator-tablist" role="tablist" aria-label="Alt issue categories">
                <button type="button" id="ada-validator-tab-empty" class="ada-validator-tab" role="tab" :aria-selected="issuesTab === 'empty'" aria-controls="ada-validator-panel-empty" @click.stop="setIssuesTab('empty')">Empty Alt Text ({{ issuesEmptyAlt.length }})</button>
                <button type="button" id="ada-validator-tab-missing" class="ada-validator-tab" role="tab" :aria-selected="issuesTab === 'missing'" aria-controls="ada-validator-panel-missing" @click.stop="setIssuesTab('missing')">Dev Issues ({{ issuesMissingAlt.length }})</button>
              </div>
              <div id="ada-validator-panel-empty" class="ada-validator-tabpanel" role="tabpanel" aria-labelledby="ada-validator-tab-empty" :hidden="issuesTab !== 'empty'">
                <AdaValidatorIssueList variant="empty" :rows="issuesEmptyAlt" @select="scrollToIssue" />
              </div>
              <div id="ada-validator-panel-missing" class="ada-validator-tabpanel" role="tabpanel" aria-labelledby="ada-validator-tab-missing" :hidden="issuesTab !== 'missing'">
                <AdaValidatorIssueList variant="missing" :rows="issuesMissingAlt" @select="scrollToIssue" />
              </div>
            </div>
            <div v-if="scanComplete" class="ada-validator-actions">
              <button type="button" class="ada-validator-btn" @click.stop="onRescan">Rescan page</button>
              <button type="button" class="ada-validator-btn ada-validator-btn--ghost" @click.stop="toggleOverlays">
                {{ overlaysVisible ? 'Hide overlay' : 'Show overlay' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
  .ada-validator-app {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50%;
    max-width: 100%;
    z-index: 2147483000;
    box-sizing: border-box;
    padding-bottom: env(safe-area-inset-bottom);
    font-family: Roboto, sans-serif;
    font-size: 14px;
  }
  .ada-validator-panel {
    width: 100%;
    box-sizing: border-box;
    background: var(--ada-validator-modal-background-color);
    border: 1px solid #fff;
    padding: 24px 24px 0 24px;
    color: var(--ada-validator-message-color);
    line-height: 1.4;
    max-height: min(70vh, 560px);
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    border-radius: 25px 25px 0 0;
    display: flex;
    flex-direction: column;
    border-bottom: 0;
  }
  .ada-validator-panel-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    margin-bottom: 24px;
    flex-shrink: 0;
  }
  .ada-validator-expand {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    flex: 0 1 auto;
    min-height: 0;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      grid-template-rows 0.5s ease,
      opacity 0.22s ease,
      visibility 0s linear 0.26s;
  }
  .ada-validator-expand.ada-validator-expand--open {
    grid-template-rows: 1fr;
    flex: 1 1 auto;
    min-height: 0;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      grid-template-rows 0.5s ease,
      opacity 0.22s ease,
      visibility 0s linear 0s;
  }
  .ada-validator-expand-inner {
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transform: translateY(8px);
    transition: transform 0.5s ease;
  }
  .ada-validator-expand.ada-validator-expand--open .ada-validator-expand-inner {
    transform: translateY(0);
  }
  @media (prefers-reduced-motion: reduce) {
    .ada-validator-expand,
    .ada-validator-expand.ada-validator-expand--open,
    .ada-validator-expand-inner {
      transition-duration: 0.01ms !important;
    }
  }
  .ada-validator-icon {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--ada-validator-message-color);
    cursor: pointer;
    font: inherit;
  }
  .ada-validator-icon:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  .ada-validator-icon :deep(svg) {
    display: block;
    width: 1.25em;
    height: 1.25em;
  }
  .ada-validator-icon :deep(polygon) {
    fill: currentColor;
  }
  .ada-validator-icon :deep(path) {
    fill: none;
    stroke: currentColor;
  }
  .ada-validator-title {
    display: block;
    font-size: 20px;
    font-weight: 700;
  }
  .ada-validator-count {
    margin-bottom: 18px;
    flex-shrink: 0;
  }
  .ada-validator-scanning {
    margin: 0;
    text-align: center;
    padding: 24px 0;
  }
  .ada-validator-scanning__glass {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    max-width: 100%;
    padding: 11px 18px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.38);
    background: rgba(255, 255, 255, 0.12);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.35),
      0 6px 20px rgba(0, 0, 0, 0.18);
    overflow: hidden;
  }
  .ada-validator-scanning__shimmer {
    position: absolute;
    inset: -40% -30%;
    background: linear-gradient(118deg, transparent 28%, rgba(255, 255, 255, 0.06) 42%, rgba(255, 255, 255, 0.35) 50%, rgba(255, 255, 255, 0.08) 58%, transparent 72%);
    background-size: 200% 100%;
    background-position: 100% 50%;
    animation: ada-validator-scan-shimmer 2.6s ease-in-out infinite;
    pointer-events: none;
    opacity: 0.95;
  }
  .ada-validator-scanning__label {
    position: relative;
    z-index: 1;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ada-validator-message-color);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  .ada-validator-scanning__dots {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .ada-validator-scanning__dot {
    display: block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.92);
    box-shadow:
      0 0 10px rgba(255, 255, 255, 0.55),
      inset 0 -1px 2px rgba(0, 0, 0, 0.12);
    animation: ada-validator-scan-dot 1.15s ease-in-out infinite;
  }
  .ada-validator-scanning__dot:nth-child(2) {
    animation-delay: 0.18s;
  }
  .ada-validator-scanning__dot:nth-child(3) {
    animation-delay: 0.36s;
  }
  @keyframes ada-validator-scan-shimmer {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  }
  @keyframes ada-validator-scan-dot {
    0%,
    70%,
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.5;
    }
    35% {
      transform: translateY(-4px) scale(1.12);
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .ada-validator-scanning__shimmer {
      animation: none;
      opacity: 0.35;
    }
    .ada-validator-scanning__dot {
      animation: none;
      opacity: 0.9;
    }
  }
  .ada-validator-issue-tabs {
    margin-bottom: 4px;
    padding: 0 24px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
  .ada-validator-tablist {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 16px;
    background: #717171;
    padding: 6px;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .ada-validator-tab {
    flex: 1 1 auto;
    min-width: 0;
    padding: 20px 12px;
    cursor: pointer;
    color: var(--ada-validator-message-color);
    font-weight: 600;
    text-align: center;
    line-height: 1.25;
  }
  .ada-validator-tab[aria-selected='true'] {
    background: var(--ada-validator-modal-background-color);
    border: 1px solid var(--ada-validator-message-color);
    border-radius: 4px;
  }
  .ada-validator-tab:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  .ada-validator-tabpanel {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: var(--ada-validator-message-color) transparent;
  }
  .ada-validator-tabpanel[hidden] {
    display: none;
  }
  .ada-validator-actions {
    color: var(--ada-validator-modal-background-color);
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    margin-top: 20px;
    margin-bottom: 24px;
    width: 100%;
    flex-shrink: 0;
    button {
      font-weight: 600;
    }
  }
  .ada-validator-btn {
    padding: 20px 25px;
    cursor: pointer;
    border: 1px solid #000;
    border-radius: 4px;
    background: var(--ada-validator-message-color);
  }
  .ada-validator-btn:hover {
    background: var(--ada-validator-btn-hover-color);
  }
  .ada-validator-btn--ghost {
    color: white;
    background: #717171;
    border: 1px solid #000;
    border-radius: 4px;
    &:hover {
      background: #717171cc;
    }
  }
</style>
